import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from '@progress/kendo-react-buttons';
import './Window.scss';

/**
 * Window
 * @module
 * @param {bool} [disabled] - Is window disabled?
 * @param {bool} [draggable] - Is window draggable?
 * @param {bool} [fullscreen] - Full screen window
 * @param {bool} [hideButtons] - Hide buttons
 * @param {bool} isOpen - Is window open?
 * @param {number|string} initialHeight - Window initial height
 * @param {number|string} initialLeft - Window initial left
 * @param {number|string} initialTop - Window initial top
 * @param {number|string} initialWidth - Window initial width
 * @param {number} level - Window level
 * @param {func} onClose - Function invoked on window close
 * @param {func} [onConfirm] - Function invoked on sumbit button click
 * @param {func} [onMove] - Function invoked on window move
 * @param {func} [onResize] - Function invoked on window resize
 * @param {func} [onStageChange] - Function invoked on window stage change
 * @param {number} relativeLeft - Window relative left
 * @param {number} relativeTop - Window relative top 
 * @param {bool} [resizeable] - Is window resizeable?
 * @param {string} [stage] - Window stage
 * @param {element} children - Children content of window
 * @param {string} title - Title text of window
 * @param {sting} [form] - Form
 * @param {string} [confirmText] - Confirm text
 * @param {string} [cancelText] - Cancel text
 * @param {string} [className] - Window class name
 */

const WindowComponent = (props) => {
  const [top, setTop] = useState(props.initialTop);
  const [left, setLeft] = useState(props.initialLeft);
  const [width, setWidth] = useState(props.initialWidth);
  const [height, setHeight] = useState(props.initialHeight || "auto");
  const [isOffset, setIsOffset] = useState(false);
  const [modal, setModal] = useState(props.modal);
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      ref.current.element.focus();
    }
    if (!isOffset && ref.current) {
      const newLeft = ref.current.windowElement.offsetLeft + (props.level !== undefined ? props.level * 30 : props.relativeLeft);
      const newTop = ref.current.windowElement.offsetTop + (props.level !== undefined ? props.level * 30 : props.relativeTop);
      setLeft(newLeft);
      setTop(newTop);
      setIsOffset(true);
    }
  }, [isOffset, props.level, props.relativeLeft, props.relativeTop]);

  const handleMove = (event) => {
    if (props.onMove) {
      props.onMove(event);
    }
    if (event.left || event.top) {
      const maxLeft = window.innerWidth - event.target.windowElement.offsetWidth;
      const maxTop = window.innerHeight - event.target.windowElement.offsetHeight;
      const newLeft = event.left > maxLeft ? maxLeft : event.left;
      const newTop = event.top > maxTop ? maxTop : event.top;
      setLeft(newLeft);
      setTop(newTop);
    }
  };

  const handleStageChange = (event) => {
    if (props.onStageChange) {
      props.onStageChange(event);
    }
    if (event.state === "MINIMIZED") {
      const newWidth = 300;
      ref.current.setState({ width: newWidth });
    }
    if (event.state === "DEFAULT") {
      const newLeft = ref.current.windowCoordinatesState.leftBeforeAction;
      const newTop = ref.current.windowCoordinatesState.topBeforeAction;
      const newWidth = ref.current.windowCoordinatesState.widthBeforeAction;
      ref.current.setState({ left: newLeft, top: newTop, width: newWidth });
      setLeft(newLeft);
      setTop(newTop);
    }
  };

  const handleResize = (event) => {
    setModal(true);
    setLeft(event.left);
    setTop(event.top);
    setWidth(event.width);
    setHeight(event.height);

    if (props.onResize) {
      props.onResize(event);
    }
    if (event.end) {
      setModal(props.modal);
    }
  };

  return (
    props.isOpen &&
    <Window
      className={props.className}
      draggable={props.draggable}
      intitialLeft={left}
      initialTop={top}
      initialWidth={width}
      initialHeight={height}
      left={left}
      top={top}
      width={width}
      height={height}
      maximizeButton={props.maximizeButton}
      minimizeButton={props.minimizeButton}
      modal={modal}
      onClose={props.onClose}
      onMove={handleMove}
      onResize={handleResize}
      onStageChange={handleStageChange}
      resizable={props.resizable}
      restoreButton={props.restoreButton}
      shouldUpdateOnDrag={false}
      stage={props.stage}
      title={props.title}
      ref={ref}
    >
      {props.children}
      {!props.hideButtons && !props.fullscreen &&
        <WindowActionsBar layout={"stretched"}>
          <Button onClick={props.onClose} key={1}>
            {props.cancelText || 'Zrušiť'}
          </Button>
          <Button
            form={props.form}
            type={props.form ? 'submit' : null}
            themeColor="primary"
            onClick={props.onConfirm}
            disabled={props.confirmDisabled}
            key={2}
          >
            {props.confirmText || 'OK'}
          </Button>
        </WindowActionsBar>
      }
    </Window>
  );

}

WindowComponent.propTypes = {
  disabled: PropTypes.bool,
  draggable: PropTypes.bool,
  fullscreen: PropTypes.bool,
  hideButtons: PropTypes.bool,
  initialHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialLeft: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOpen: PropTypes.bool.isRequired,
  level: PropTypes.number,
  maximizeButton: PropTypes.bool,
  minimizeButton: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onMove: PropTypes.func,
  onResize: PropTypes.func,
  onStageChange: PropTypes.func,
  relativeLeft: PropTypes.number,
  relativeTop: PropTypes.number,
  resizable: PropTypes.bool,
  restoreButton: PropTypes.bool,
  stage: PropTypes.string,
  title: PropTypes.any,
}

export default WindowComponent;